import { useState } from 'react';
import { reports, scheduledReports } from '../../mocks/poolData';

export default function ReportesDinamicos() {
  const [reportType, setReportType] = useState('diario');
  const [selectedParameters, setSelectedParameters] = useState(['ph', 'freeChlorine', 'alkalinity']);
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'csv' | 'excel'>('pdf');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeComparison, setIncludeComparison] = useState(false);
  const [includeRecommendations, setIncludeRecommendations] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    // Simular generación de reporte
    setTimeout(() => {
      setIsGenerating(false);
      alert('Reporte generado exitosamente');
    }, 3000);
  };

  const handleParameterToggle = (param: string) => {
    setSelectedParameters(prev => 
      prev.includes(param) 
        ? prev.filter(p => p !== param)
        : [...prev, param]
    );
  };

  const formatFileSize = (size: string) => size;
  const formatDate = (date: string) => new Date(date).toLocaleDateString('es-ES');

  return (
    <section id="reportes" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Reportes Dinámicos</h2>
          <p className="text-xl text-slate-600">Genera reportes personalizados con análisis avanzados</p>
        </div>

        <div className="space-y-8">
          {/* Constructor de Reportes */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-100 rounded-3xl p-8">
            <div className="flex items-center mb-8">
              <i className="ri-file-add-line text-4xl text-indigo-600 mr-4"></i>
              <h3 className="text-2xl font-bold text-slate-800">Generar Nuevo Reporte</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Configuración del Reporte */}
              <div className="space-y-6">
                {/* Tipo de Reporte */}
                <div>
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">1. Selecciona el Tipo de Reporte</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { id: 'diario', label: 'Diario', icon: 'ri-calendar-line' },
                      { id: 'semanal', label: 'Semanal', icon: 'ri-calendar-2-line' },
                      { id: 'mensual', label: 'Mensual', icon: 'ri-calendar-event-line' },
                      { id: 'personalizado', label: 'Personalizado', icon: 'ri-settings-3-line' }
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => setReportType(type.id)}
                        className={`p-4 rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${
                          reportType === type.id
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'
                        }`}
                      >
                        <i className={`${type.icon} text-2xl mb-2 block`}></i>
                        <span className="font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Parámetros a Incluir */}
                <div>
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">2. Parámetros a Incluir</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'ph', label: 'pH', icon: 'ri-test-tube-line' },
                      { id: 'alkalinity', label: 'Alcalinidad', icon: 'ri-scales-3-line' },
                      { id: 'freeChlorine', label: 'Cloro Libre', icon: 'ri-drop-line' },
                      { id: 'hardness', label: 'Dureza', icon: 'ri-crystal-line' },
                      { id: 'stabilizer', label: 'Estabilizante', icon: 'ri-shield-line' },
                      { id: 'phosphates', label: 'Fosfatos', icon: 'ri-plant-line' }
                    ].map(param => (
                      <label key={param.id} className="flex items-center space-x-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedParameters.includes(param.id)}
                          onChange={() => handleParameterToggle(param.id)}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <i className={`${param.icon} text-indigo-600`}></i>
                        <span className="text-sm font-medium text-slate-700">{param.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Formato de Salida */}
                <div>
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">3. Formato de Salida</h4>
                  <div className="flex space-x-4">
                    {[
                      { id: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line', color: 'text-red-500' },
                      { id: 'csv', label: 'CSV', icon: 'ri-table-line', color: 'text-green-500' },
                      { id: 'excel', label: 'Excel', icon: 'ri-file-excel-line', color: 'text-green-600' }
                    ].map(format => (
                      <button
                        key={format.id}
                        onClick={() => setOutputFormat(format.id as typeof outputFormat)}
                        className={`flex items-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${
                          outputFormat === format.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 bg-white hover:border-indigo-300'
                        }`}
                      >
                        <i className={`${format.icon} text-xl ${format.color}`}></i>
                        <span className="font-medium">{format.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vista Previa y Opciones Avanzadas */}
              <div className="space-y-6">
                {/* Opciones Avanzadas */}
                <div className="bg-white rounded-2xl p-6">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">4. Opciones Avanzadas</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeCharts}
                        onChange={(e) => setIncludeCharts(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <i className="ri-bar-chart-line text-indigo-600"></i>
                      <span className="text-sm font-medium text-slate-700">Incluir gráficos</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeComparison}
                        onChange={(e) => setIncludeComparison(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <i className="ri-git-compare-line text-indigo-600"></i>
                      <span className="text-sm font-medium text-slate-700">Comparativa con período anterior</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeRecommendations}
                        onChange={(e) => setIncludeRecommendations(e.target.checked)}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <i className="ri-lightbulb-line text-indigo-600"></i>
                      <span className="text-sm font-medium text-slate-700">Recomendaciones automáticas</span>
                    </label>
                  </div>
                </div>

                {/* Vista Previa */}
                <div className="bg-white rounded-2xl p-6">
                  <h4 className="text-lg font-semibold text-slate-700 mb-4">Vista Previa</h4>
                  <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-200">
                    <div className="text-center text-gray-500">
                      <i className="ri-eye-line text-3xl mb-2"></i>
                      <p className="text-sm">Vista previa del reporte</p>
                      <p className="text-xs mt-1">
                        {reportType.charAt(0).toUpperCase() + reportType.slice(1)} • {selectedParameters.length} parámetros • {outputFormat.toUpperCase()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botón de Generación */}
                <button
                  onClick={handleGenerateReport}
                  disabled={isGenerating || selectedParameters.length === 0}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    isGenerating || selectedParameters.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 transform hover:scale-105 shadow-lg'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Generando Reporte...
                    </>
                  ) : (
                    <>
                      <i className="ri-download-line mr-2"></i>
                      Generar Reporte
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Reportes Programados */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <i className="ri-time-line text-3xl text-orange-500 mr-3"></i>
                <h3 className="text-2xl font-bold text-slate-800">Reportes Programados</h3>
              </div>
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-add-line mr-1"></i>
                Nuevo Programado
              </button>
            </div>

            <div className="space-y-4">
              {scheduledReports.map((scheduled) => (
                <div key={scheduled.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border-l-4 border-orange-500">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="font-semibold text-slate-800">{scheduled.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        scheduled.frequency === 'Diario' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {scheduled.frequency}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      Próxima ejecución: <span className="font-medium">{scheduled.nextExecution}</span>
                    </p>
                    <div className="flex items-center mt-2">
                      <span className="text-xs text-slate-500 mr-2">Destinatarios:</span>
                      <div className="flex -space-x-1">
                        {scheduled.recipients.map((email, index) => (
                          <div 
                            key={index}
                            className="w-6 h-6 bg-orange-400 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                            title={email}
                          >
                            {email.charAt(0).toUpperCase()}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg transition-colors cursor-pointer">
                      <i className="ri-edit-line"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg transition-colors cursor-pointer">
                      <i className="ri-pause-line"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Historial de Reportes */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <i className="ri-history-line text-3xl text-teal-500 mr-3"></i>
              <h3 className="text-2xl font-bold text-slate-800">Historial de Reportes</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 rounded-lg">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 rounded-l-lg">Fecha</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Período</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Formato</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tamaño</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 rounded-r-lg">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDate(report.generatedDate)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          report.type === 'Semanal' ? 'bg-blue-100 text-blue-800' :
                          report.type === 'Mensual' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {report.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{report.period}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <i className={`${
                            report.format === 'PDF' ? 'ri-file-pdf-line text-red-500' :
                            report.format === 'Excel' ? 'ri-file-excel-line text-green-600' :
                            'ri-table-line text-green-500'
                          }`}></i>
                          <span className="text-sm text-gray-900">{report.format}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{formatFileSize(report.size)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                            title="Descargar"
                          >
                            <i className="ri-download-cloud-line"></i>
                          </button>
                          <button 
                            className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            title="Vista previa"
                          >
                            <i className="ri-eye-line"></i>
                          </button>
                          <button 
                            className="w-8 h-8 flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-lg transition-colors cursor-pointer"
                            title="Compartir"
                          >
                            <i className="ri-share-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">1</span> a <span className="font-medium">3</span> de{' '}
                <span className="font-medium">{reports.length}</span> reportes
              </p>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  Anterior
                </button>
                <button className="px-3 py-2 text-sm text-white bg-teal-600 border border-teal-600 rounded-lg hover:bg-teal-700 cursor-pointer">
                  1
                </button>
                <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}